import { type IntlShape } from "react-intl";
import { z } from "zod";

import { machineStatusSchema } from "../machine-status";
import { machineTypeSchema } from "../machine-type";
import { productionStatusSchema } from "../machine-production-status";
import { getMachineRequestBaseSchema, type MachineRequestBase } from "../machine-request-base";

export const emUnitSchema = z.enum(["mm", "inch"]);

const emActiveLongHeadSchema = z
	.object({
		number: z.number(),
		leftSideToTool: z.number().optional(),
		position: z.number().optional(),
		minimumPosition: z.number().optional(),
		maximumPosition: z.number().optional(),
		width: z.number().optional(),
		toolToSensorOffset: z.number().optional(),
		leftSideToWasteSeparator: z.number().optional(),
		wasteSeparatorWidth: z.number().optional(),
	})
	.passthrough();

export const emLongHeadSchema = z
	.object({
		longHeadYOffset: z.number().optional(),
		connectDelayOff: z.number().optional(),
		connectDelayOn: z.number().optional(),
		creaseDelayOff: z.number().optional(),
		creaseDelayOn: z.number().optional(),
		cutDelayOff: z.number().optional(),
		cutDelayOn: z.number().optional(),
		outOfPositionWarningThreshold: z.number().optional(),
		positionInWindow: z.number().optional(),
		speedInPercent: z.number().optional(),
		accelerationInPercent: z.number().optional(),
		torque: z.number().optional(),
		wasteSeparatorWidth: z.number().optional(),
		wasteSeparatorActivationOffset: z.number().optional(),
		wasteSeparatorDeactivationOffset: z.number().optional(),
		longHeads: z.array(emActiveLongHeadSchema).optional(),
		maximumPosition: z.number().optional(),
		minimumPosition: z.number().optional(),
		longHeadWidth: z.number().optional(),
		connectDelayOffInSeconds: z.number().optional(),
		connectDelayOnInSeconds: z.number().optional(),
		creaseCompensation: z.number().optional(),
		cutCompensation: z.number().optional(),
	})
	.passthrough();

const emCrossHeadSchema = z
	.object({
		position: z.number().optional(),
		maxHeadPosition: z.number().optional(),
		minHeadPosition: z.number().optional(),
		connectDelayOff: z.number().optional(),
		connectDelayOn: z.number().optional(),
		creaseDelayOff: z.number().optional(),
		creaseDelayOn: z.number().optional(),
		cutDelayOff: z.number().optional(),
		cutDelayOn: z.number().optional(),
		referenceQuantity: z.number().optional(),
		toolPositionToSensor: z.number().optional(),
		speedInPercent: z.number().optional(),
		accelerationInPercent: z.number().optional(),
		torque: z.number().optional(),
		outOfPositionWarningThreshold: z.number().optional(),
		creaseCompensation: z.number().optional(),
		cutCompensation: z.number().optional(),
	})
	.passthrough();

const emFeedRollerSchema = z
	.object({
		outFeedLength: z.number().optional(),
		maximumReverseDistance: z.number().optional(),
		toolsGearRatio: z.number().optional(),
		rollerStartingPositions: z
			.array(
				z
					.object({
						trackNumber: z.number(),
						position: z.number(),
					})
					.passthrough(),
			)
			.optional(),
		rollerPackStationTimers: z
			.array(
				z
					.object({
						trackNumber: z.number(),
						packStation1Timer: z.number(),
						packStation2Timer: z.number(),
					})
					.passthrough(),
			)
			.optional(),
		speedInPercent: z.number().optional(),
		accelerationInPercent: z.number().optional(),
		reverseSpeedInPercent: z.number().optional(),
		reverseAccelerationInPercent: z.number().optional(),
		torque: z.number().optional(),
		reverseTorque: z.number().optional(),
	})
	.passthrough();

const emCodeGeneratorSchema = z
	.object({
		minimumLineDistanceToCorrugateEdge: z.number().optional(),
		minimumLineLength: z.number().optional(),
	})
	.passthrough();

export const emPhysicalMachineSettingsSchema = z
	.object({
		unit: emUnitSchema,
		onTheFlyActive: z.boolean().catch(false),
		hasPackTags: z.boolean().catch(false),
		longHead: emLongHeadSchema,
		corrugateWidthTolerance: z.number().optional(),
		trackChangeDelay: z.number().optional(),
		trackServiceFeedingDistance: z.number().optional(),
		useCorrugateSensors: z.boolean().optional(),
		enableDesignTrim: z.boolean().optional(),
		enableSideTrim: z.boolean().optional(),
		minimumWasteLength: z.number().optional(),
		maximumWasteLength: z.number().optional(),
		minimumWasteWidth: z.number().optional(),
		maximumWasteWidth: z.number().optional(),
		crossHead: emCrossHeadSchema.optional(),
		feedRoller: emFeedRollerSchema.optional(),
		codeGenerator: emCodeGeneratorSchema.optional(),
	})
	.passthrough();

const emTrackSchema = z
	.object({
		number: z.number().optional(),
		trackNumber: z.number().optional(),
		trackOffset: z.number(),
	})
	.passthrough();

export const getEmMachineRequestSchema = (intl: IntlShape, existingMachines: MachineRequestBase[] = []) =>
	getMachineRequestBaseSchema(intl, existingMachines).extend({
		status: machineStatusSchema.optional(),
		productionStatus: productionStatusSchema.optional(),
		machineType: z.literal(machineTypeSchema.enum.Em),
		assetId: z.string().optional(),
		crossConveyorConnected: z.boolean(),
		physicalMachineSettings: emPhysicalMachineSettingsSchema,
		releaseBehavior: z.enum(["Automatic", "ReleaseCarton", "RequestCarton"]),
		releaseCartonWithFootPedal: z.boolean(),
		tracks: z.array(emTrackSchema).min(1).max(4),
	});

export type EmMachineRequest = z.infer<ReturnType<typeof getEmMachineRequestSchema>>;
export type EmPhysicalMachineSettings = z.infer<typeof emPhysicalMachineSettingsSchema>;
